// ============================================================
// Pagina clientului deschisa din linkul de aprobare: /aprobare/:cod (19 octombrie 2026,
// APROBARE_CLIENT_PLAN.md §3 si §4.3; macheta acceptata de Luminita pe 19.10).
//
// Fara cont si fara sesiune: tot ce stie pagina vine de la functia `aprobare-link`, pe baza
// codului din adresa. Gandita intai pentru telefon; contrast plin, fara gri deschis.
// Butoanele (pasul 4): Aprob trimite pe loc; Modific textul si Cer schimbari deschid un camp sub
// postare. Nimic de aici nu publica: raspunsul ajunge la agentie, cu alerta.
//
// Limba: cea aleasa in aplicatie, daca exista; altfel limba browserului (engleza sau romana).
// Pagina nu intra in motoarele de cautare (noindex) si nu trimite adresa ei, cu codul, mai
// departe la serverele de unde vin pozele (no-referrer).
// ============================================================
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { tLimba } from './i18n';
import { IconRetea } from './IconRetea';

const ADRESA_FUNCTIE = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/aprobare-link`;

const C = { verde: '#1A6B3C', verdeInchis: '#0F3D22', margine: '#D1E8DA', fond: '#F2F8F4', deschis: '#E8F5EE', text: '#111827', text2: '#374151', rosu: '#B91C1C', galben: '#8F5310' };
const NUME_RETEA = { facebook: 'Facebook', instagram: 'Instagram', tiktok: 'TikTok', linkedin: 'LinkedIn', threads: 'Threads', x: 'X', youtube: 'YouTube' };

function limbaPaginii() {
  try {
    const aleasa = localStorage.getItem('nm_lang');
    if (aleasa === 'ro' || aleasa === 'en') return aleasa;
  } catch (_) { /* fara stocare: limba browserului */ }
  return (navigator.language || 'ro').toLowerCase().startsWith('ro') ? 'ro' : ((navigator.language || '').toLowerCase().startsWith('en') ? 'en' : 'ro');
}

// O eticheta in <head> pusa cat timp e pagina deschisa.
function useMeta(nume, continut) {
  useEffect(() => {
    const m = document.createElement('meta');
    m.name = nume;
    m.content = continut;
    document.head.appendChild(m);
    return () => { m.remove(); };
  }, [nume, continut]);
}

function dataSiOra(p, lang) {
  if (!p.data) return null;
  const [a, l, z] = p.data.split('-').map(Number);
  const d = new Date(a, l - 1, z);
  const zi = d.toLocaleDateString(lang === 'en' ? 'en-GB' : 'ro-RO', { weekday: 'long', day: 'numeric', month: 'long' });
  return `${zi.charAt(0).toUpperCase()}${zi.slice(1)}${p.ora ? ` · ${p.ora}` : ''}`;
}

function Media({ p, t }) {
  const [i, setI] = useState(0);
  if (p.video) {
    return (
      <video src={p.video} poster={p.coperta || undefined} controls playsInline preload="metadata"
        style={{ width: '100%', maxHeight: 520, display: 'block', background: '#000' }} />
    );
  }
  if (!p.imagini.length) return null;
  const n = p.imagini.length;
  return (
    <div style={{ position: 'relative', background: '#F3F4F6' }}>
      <img src={p.imagini[i]} alt={n > 1 ? t('poza', { i: i + 1, n }) : ''} referrerPolicy="no-referrer"
        style={{ width: '100%', maxHeight: 520, objectFit: 'contain', display: 'block' }} />
      {n > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: '8px 0' }}>
          {p.imagini.map((_, k) => (
            <button key={k} type="button" aria-label={t('poza', { i: k + 1, n })} onClick={() => setI(k)}
              style={{ width: 12, height: 12, borderRadius: '50%', border: `1.5px solid ${C.verde}`, background: k === i ? C.verde : 'white', padding: 0, cursor: 'pointer' }} />
          ))}
        </div>
      )}
    </div>
  );
}

const buton = { borderRadius: 10, padding: 12, font: "600 15px 'DM Sans', sans-serif", border: `1.5px solid ${C.verde}`, cursor: 'pointer', width: '100%' };

function Stare({ r, t }) {
  if (r.actiune === 'aprobat') {
    return <div style={{ margin: '10px 14px 14px', borderRadius: 10, padding: '10px 12px', background: C.deschis, color: C.verdeInchis, fontWeight: 600 }}>
      {t('stareAprobat')}<div style={{ fontWeight: 400, color: C.text2, fontSize: 13, marginTop: 3 }}>{t('stareAprobatTxt')}</div>
    </div>;
  }
  if (r.actiune === 'modificat') {
    return <div style={{ margin: '10px 14px 14px', borderRadius: 10, padding: '10px 12px', background: '#FEF3C7', color: C.galben, fontWeight: 600 }}>
      {t('stareModificat')}<div style={{ fontWeight: 400, color: C.text2, fontSize: 13, marginTop: 3 }}>{t('stareModificatTxt')}</div>
    </div>;
  }
  return <div style={{ margin: '10px 14px 14px', borderRadius: 10, padding: '10px 12px', background: '#FDECEC', color: C.rosu, fontWeight: 600 }}>
    {t('stareCerere')}{r.comentariu && <div style={{ fontWeight: 400, color: C.text2, fontSize: 13, marginTop: 3 }}>„{r.comentariu}”</div>}
  </div>;
}

const zona = { width: '100%', border: `1.5px solid ${C.verde}`, borderRadius: 10, padding: 10, font: "15px/1.5 'DM Sans', sans-serif", color: C.text, resize: 'vertical', boxSizing: 'border-box' };

// Cele trei butoane si ce se deschide sub ele. `trimite` intoarce null daca a mers, altfel textul erorii.
function Raspuns({ p, t, trimite }) {
  const [mod, setMod] = useState(null);           // null | 'modific' | 'cer'
  const [text, setText] = useState(p.text);
  const [com, setCom] = useState('');
  const [seTrimite, setSeTrimite] = useState(false);
  const [eroare, setEroare] = useState('');

  const fa = async (actiune, extra) => {
    setEroare('');
    setSeTrimite(true);
    const e = await trimite(p, actiune, extra);
    setSeTrimite(false);
    if (e) setEroare(e);
  };

  const trimiteModificarea = () => {
    if (!text.trim()) return setEroare(t('textGol'));
    if (text === p.text) return setEroare(t('textNeschimbat'));
    fa('modifica', { text_nou: text });
  };
  const trimiteCererea = () => {
    if (!com.trim()) return setEroare(t('comentariuGol'));
    fa('cere_schimbari', { comentariu: com });
  };
  const renunta = () => { setMod(null); setEroare(''); setText(p.text); setCom(''); };
  const mesajEroare = eroare && <p role="alert" style={{ color: C.rosu, fontSize: 14, fontWeight: 600, margin: 0 }}>{eroare}</p>;

  if (mod) {
    const eModific = mod === 'modific';
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 14px 14px' }}>
        <label style={{ fontSize: 14, fontWeight: 600, color: C.text2 }}>
          {eModific ? t('modificaEticheta') : t('cerEticheta')}
          <textarea value={eModific ? text : com} onChange={e => (eModific ? setText : setCom)(e.target.value)}
            rows={eModific ? Math.min(12, Math.max(4, text.split('\n').length + 1)) : 3} disabled={seTrimite}
            style={{ ...zona, marginTop: 6, ...(eModific ? {} : { borderColor: C.rosu }) }} autoFocus />
        </label>
        {mesajEroare}
        <button type="button" disabled={seTrimite} onClick={eModific ? trimiteModificarea : trimiteCererea}
          style={{ ...buton, background: eModific ? C.verde : C.rosu, borderColor: eModific ? C.verde : C.rosu, color: 'white' }}>
          {seTrimite ? t('seTrimite') : (eModific ? t('trimiteModificarea') : t('trimiteCererea'))}
        </button>
        <button type="button" disabled={seTrimite} onClick={renunta} style={{ ...buton, background: 'white', color: C.text2, borderColor: C.margine }}>{t('renunt')}</button>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 14px 14px' }}>
      {mesajEroare}
      <button type="button" disabled={seTrimite} onClick={() => fa('aproba')} style={{ ...buton, background: C.verde, color: 'white' }}>{seTrimite ? t('seTrimite') : t('aprob')}</button>
      <button type="button" disabled={seTrimite} onClick={() => setMod('modific')} style={{ ...buton, background: 'white', color: C.verde }}>{t('modific')}</button>
      <button type="button" disabled={seTrimite} onClick={() => setMod('cer')} style={{ ...buton, background: 'white', color: C.rosu, borderColor: C.rosu }}>{t('cerSchimbari')}</button>
    </div>
  );
}

// Postarea publicata (20.10.2026, macheta aleasa de Luminita): banda verde plina, cu ziua si ora
// publicarii si, unde reteaua ne da adresa, „Vezi postarea pe ...".
function Publicat({ p, t, lang }) {
  const cand = p.publicat_la ? new Date(p.publicat_la).toLocaleString(lang === 'en' ? 'en-GB' : 'ro-RO', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }) : '';
  const retea = NUME_RETEA[p.retea] || p.retea;
  return (
    <div style={{ margin: '10px 14px 14px', borderRadius: 10, padding: '10px 12px', background: C.verde, color: 'white', fontWeight: 600 }}>
      {t('statePublicat')}
      <div style={{ fontWeight: 400, color: C.deschis, fontSize: 13, marginTop: 3 }}>
        {cand ? `${cand.charAt(0).toUpperCase()}${cand.slice(1)}` : ''}{!p.link_public ? `${cand ? ' · ' : ''}${t('peRetea', { retea })}` : ''}
      </div>
      {p.link_public && (
        <a href={p.link_public} target="_blank" rel="noopener noreferrer"
          style={{ display: 'inline-block', marginTop: 8, background: 'white', color: C.verde, borderRadius: 8, padding: '7px 12px', fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>
          {t('veziPostarea', { retea })}
        </a>
      )}
    </div>
  );
}

function Card({ p, t, lang, trimite }) {
  const cand = dataSiOra(p, lang);
  const text = p.raspuns && p.raspuns.actiune === 'modificat' && p.raspuns.text_nou ? p.raspuns.text_nou : p.text;
  return (
    <article style={{ background: 'white', border: `1.5px solid ${C.margine}`, borderRadius: 14, overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '10px 14px', fontSize: 14, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 7 }}>
          <IconRetea id={p.retea} size={16} />{NUME_RETEA[p.retea] || p.retea}{p.format ? ` · ${p.format}` : ''}
        </span>
        <span style={{ color: C.text2, fontWeight: 500 }}>{cand || t('faraData')}</span>
      </div>
      <Media p={p} t={t} />
      <div style={{ padding: '12px 14px 4px', fontSize: 15, lineHeight: 1.55, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', color: C.text }}>{text}</div>
      {p.publicat ? <Publicat p={p} t={t} lang={lang} /> : p.raspuns ? <Stare r={p.raspuns} t={t} /> : (
        <Raspuns p={p} t={t} trimite={trimite} />
      )}
    </article>
  );
}

function Mesaj({ titlu, text, t, buton: b }) {
  return (
    <div style={{ padding: '96px 28px', textAlign: 'center' }}>
      <div style={{ fontSize: 44 }} aria-hidden="true">🔒</div>
      <h1 style={{ fontSize: 20, margin: '14px 0 8px', color: C.text }}>{titlu}</h1>
      <p style={{ color: C.text2, fontSize: 15, lineHeight: 1.55, margin: 0 }}>{text}</p>
      {b}
    </div>
  );
}

export default function AprobareClient() {
  const { cod } = useParams();
  const [lang] = useState(limbaPaginii);
  const t = useCallback((cheie, vars) => tLimba(lang, 'aprobareLink', cheie, vars), [lang]);
  const [stare, setStare] = useState({ faza: 'incarca' });
  useMeta('robots', 'noindex, nofollow');
  useMeta('referrer', 'no-referrer');

  const incarca = useCallback(async () => {
    setStare({ faza: 'incarca' });
    try {
      const r = await fetch(ADRESA_FUNCTIE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ actiune: 'citeste', cod }),
      });
      if (r.status === 404) return setStare({ faza: 'nevalid' });
      const d = await r.json();
      if (!r.ok || !d.ok) return setStare({ faza: 'eroare' });
      setStare({ faza: 'gata', date: d });
    } catch (_) {
      setStare({ faza: 'eroare' });
    }
  }, [cod]);

  useEffect(() => { incarca(); }, [incarca]);

  // Raspunsul la o postare. Daca postarea s-a schimbat intre timp (sau linkul nu mai merge),
  // pagina se reincarca; altfel cardul arata pe loc ce a ales clientul.
  const trimite = useCallback(async (p, actiune, extra = {}) => {
    try {
      const r = await fetch(ADRESA_FUNCTIE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ actiune, cod, postare_id: p.id, text_vazut: p.text, ...extra }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.status === 404 && d.error === 'link_nevalid') { setStare({ faza: 'nevalid' }); return null; }
      if (r.status === 409) { setTimeout(incarca, 2500); return t('sSchimbat'); }
      if (!r.ok || !d.ok) return t('eroareTrimitere');
      setStare(s => (s.faza !== 'gata' ? s : {
        ...s, date: { ...s.date, postari: s.date.postari.map(x => (x.id === p.id ? { ...x, asteapta: false, raspuns: d.raspuns } : x)) },
      }));
      return null;
    } catch (_) {
      return t('eroareTrimitere');
    }
  }, [cod, incarca, t]);
  useEffect(() => {
    document.documentElement.lang = lang;
    const vechi = document.title;
    document.title = t('titlu');
    return () => { document.title = vechi; };
  }, [lang, t]);

  const d = stare.date;
  const n = d ? d.postari.length : 0;
  const raspunse = d ? d.postari.filter(p => p.raspuns || p.publicat).length : 0;
  const publicate = d ? d.postari.filter(p => p.publicat).length : 0;
  const pana = d ? new Date(d.expira_la).toLocaleDateString(lang === 'en' ? 'en-GB' : 'ro-RO', { day: 'numeric', month: 'long' }) : '';

  return (
    <div style={{ minHeight: '100vh', background: C.fond, fontFamily: "'DM Sans', sans-serif", color: C.text }}>
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <header style={{ background: 'white', borderBottom: `1.5px solid ${C.margine}`, padding: '16px 16px 14px', position: 'sticky', top: 0, zIndex: 2 }}>
          <div style={{ fontFamily: "'DM Serif Display', serif", color: C.verdeInchis, fontSize: 15 }}>{t('marca')}</div>
          {stare.faza === 'gata' && (
            <>
              <div style={{ fontSize: 21, fontWeight: 700, marginTop: 6 }}>{d.client}</div>
              <div style={{ fontSize: 15, color: C.text2, marginTop: 3 }}>
                {publicate > 0
                  ? (publicate === n ? t('toatePublicate', { n }) : t('unelePublicate', { p: publicate, n }))
                  : raspunse === 0 ? (n === 1 ? t('deAprobatUna') : t('deAprobat', { n })) : (raspunse === n ? t('totulGata') : t('aRaspuns', { r: raspunse, n }))}
              </div>
              <span style={{ display: 'inline-block', marginTop: 8, fontSize: 13, fontWeight: 600, color: C.verde, background: C.deschis, borderRadius: 20, padding: '3px 10px' }}>
                {t('valabil', { data: pana })}
              </span>
            </>
          )}
        </header>

        {stare.faza === 'incarca' && <p style={{ padding: '48px 16px', textAlign: 'center', color: C.text2, fontSize: 15 }}>{t('seIncarca')}</p>}
        {stare.faza === 'nevalid' && <Mesaj titlu={t('nevalidTitlu')} text={t('nevalidText')} t={t} />}
        {stare.faza === 'eroare' && (
          <Mesaj titlu={t('eroareTitlu')} text={t('eroareText')} t={t}
            buton={<button type="button" onClick={incarca} style={{ ...buton, width: 'auto', padding: '11px 22px', marginTop: 20, background: C.verde, color: 'white' }}>{t('reincearca')}</button>} />
        )}
        {stare.faza === 'gata' && (n === 0
          ? <Mesaj titlu={t('nevalidTitlu')} text={t('nevalidText')} t={t} />
          : (
            <main style={{ padding: '14px 12px 32px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              {d.postari.map(p => <Card key={p.id} p={p} t={t} lang={lang} trimite={trimite} />)}
            </main>
          ))}
      </div>
    </div>
  );
}
